import { useEffect, useRef } from "react";
import { useChatsStore } from "../store/chats";
import { unlockAudio, enqueue, cancelAll, isConversationActive } from "../lib/globalTts";

/**
 * useAutoSpeak — reads the assistant's response aloud as it streams.
 *
 * All audio playback is delegated to globalTts so this hook can never
 * overlap with the manual "🔊 speak" button or the conversation overlay.
 *
 * Strategy:
 *   • While streaming, poll the active session every 300 ms.
 *   • When a sentence boundary appears in the latest assistant message,
 *     enqueue the new chunk to globalTts.
 *   • A new assistant message id => cancelAll() + reset spoken cursor.
 *   • When streaming ends, flush any remaining tail.
 */

let _poll: ReturnType<typeof setInterval> | null = null;
let _spoken = 0;
let _lastMsgId = "";

function sanitize(t: string): string {
  return t
    .replace(/```[\s\S]*?```/g, " ")
    .replace(/`([^`]+)`/g, "$1")
    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
    .replace(/https?:\/\/\S+/g, "")
    .replace(/\*\*([^*]+)\*\*/g, "$1")
    .replace(/\*([^*]+)\*/g, "$1")
    .replace(/__([^_]+)__/g, "$1")
    .replace(/^#+\s+/gm, "")
    .replace(/\n{2,}/g, ". ")
    .replace(/\n/g, " ")
    .replace(/\s{2,}/g, " ")
    .trim();
}

function pollStore() {
  // Conversation overlay owns TTS while open — don't double-speak.
  if (isConversationActive()) return;
  const st = useChatsStore.getState();
  const sess = st.sessions.find((s) => s.id === st.activeId);
  if (!sess) return;
  const last = sess.messages[sess.messages.length - 1];
  if (!last || last.role !== "assistant" || !last.text) return;

  // New assistant message — reset and cancel any in-flight audio.
  if (last.id !== _lastMsgId) {
    cancelAll();
    _lastMsgId = last.id;
    _spoken = 0;
    unlockAudio();
  }

  const full = sanitize(last.text);
  const fresh = full.slice(_spoken);
  if (!fresh) return;

  // Find the last sentence boundary inside the fresh tail.
  let end = 0;
  const re = /[.!?]\s/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(fresh)) !== null) end = m.index + 1;
  if (end <= 0) return;

  const chunk = fresh.slice(0, end).trim();
  if (chunk.length <= 5) return;

  _spoken += end;
  enqueue(chunk);
}

function stopPolling() {
  if (_poll) { clearInterval(_poll); _poll = null; }
}

function flushTail() {
  if (isConversationActive()) return; // overlay owns TTS — skip tail.
  const st = useChatsStore.getState();
  const sess = st.sessions.find((s) => s.id === st.activeId);
  if (!sess) return;
  const last = sess.messages[sess.messages.length - 1];
  if (!last || last.role !== "assistant" || last.id !== _lastMsgId) return;
  const rem = sanitize(last.text || "").slice(_spoken).trim();
  if (rem.length > 5) {
    _spoken += rem.length;
    enqueue(rem);
  }
}

export function useAutoSpeak(enabled: boolean = true) {
  const streaming = useChatsStore((s) => {
    const sess = s.sessions.find((ss) => ss.id === s.activeId);
    return sess?.status === "streaming";
  });
  const prevStreaming = useRef(false);

  useEffect(() => {
    // Авто-озвучка выключена — ничего не синтезируем (экономия Yandex TTS).
    if (!enabled) {
      stopPolling();
      prevStreaming.current = streaming;
      return;
    }
    // Streaming just started — start polling.
    if (streaming && !prevStreaming.current) {
      stopPolling();
      _poll = setInterval(pollStore, 300);
    }
    // Streaming just ended — flush remainder + stop polling.
    if (!streaming && prevStreaming.current) {
      const t = setTimeout(() => {
        flushTail();
        stopPolling();
      }, 400);
      prevStreaming.current = streaming;
      return () => clearTimeout(t);
    }
    prevStreaming.current = streaming;
  }, [streaming, enabled]);

  // Cleanup on unmount: stop polling but DO NOT cancelAll() so
  // any audio still draining can finish naturally.
  useEffect(() => {
    return () => { stopPolling(); };
  }, []);
}
